import React, { FC, SyntheticEvent, useCallback, useState, useEffect, PropsWithChildren } from "react"
import { Grid, Tab, Tabs, Box } from "@mui/material"
import { useLocation } from "react-router-dom"
import { useQueryParams } from "src/hooks/ui"

const buildStatus = (status: string) => status.split(" ").join("")

interface TabPanelProps {
  index: number;
  value: number;
}

const TabPanel: FC<PropsWithChildren<TabPanelProps>> = ({ index, value, children }) => {
  return index === value ? (
    <Box mt={2} height="inherit">
      {children}
    </Box>
  ) : null
}

export interface Status {
  status: string;
  disabled?: boolean;
  content: React.ReactElement;
  extraLabel?: string;
}

interface TabGroupFilterProps {
  name: string;
  statusList: Status[];
  paramsToRemove?: string[];
  paramsToKeep?: string[];
  onChange?: (status: string) => void;
}

export const TabGroupFilter: FC<TabGroupFilterProps> = ({
  name,
  statusList,
  paramsToRemove = [],
  paramsToKeep,
  onChange,
}) => {
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const statusQueryParam = queryParams.get(name)

  const validStatuses = statusList.map((el) => buildStatus(el.status))
  const validatedParam = statusQueryParam && validStatuses.includes(buildStatus(statusQueryParam))
    ? statusQueryParam
    : null
  const initial = buildStatus(validatedParam || statusList[0]?.status || "")
  const buildState = statusList.length > 1 ? useQueryParams : useState
  const [params, setParams] = buildState({ [name]: initial })
  const currentIndex = statusList.findIndex(
    (el) => buildStatus(el.status) === params[name]
  )
  const current = currentIndex !== -1 ? currentIndex : 0

  useEffect(() => {
    if (statusQueryParam && validatedParam === null && statusList.length > 0) {
      setParams({ [name]: buildStatus(statusList[0].status) })
    }
  }, [statusQueryParam, statusList.length]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = useCallback(
    async (_: SyntheticEvent, index: number) => {
      let paramsToRemoveNormalized: string[]
      if (paramsToRemove.length > 0) {
        paramsToRemoveNormalized = [name, ...paramsToRemove]
      } else if (paramsToKeep) {
        paramsToRemoveNormalized = Object.keys(params).filter((key) => !paramsToKeep.includes(key))
      } else {
        paramsToRemoveNormalized = [name]
      }
      await setParams({ [name]: buildStatus(statusList[index].status) }, paramsToRemoveNormalized)
      if (onChange) onChange(statusList[index].status)
    },
    [name, paramsToRemove, paramsToKeep, params, setParams, statusList, onChange]
  )

  if (!statusList.length) return null

  return (
    <Grid alignItems="stretch" container direction="column" wrap="nowrap">
      <Grid item xs minWidth={0}>
        <Tabs
          sx={{ borderBottom: 1, borderColor: "divider" }}
          value={current}
          onChange={handleChange}
          variant="scrollable"
          allowScrollButtonsMobile
        >
          {statusList.map(({ status, disabled, extraLabel }) => (
            <Tab key={status} disabled={disabled} label={extraLabel ? `${status} (${extraLabel})` : status} />
          ))}
        </Tabs>
      </Grid>
      <Grid item xs width="100%" height="100%" minWidth={0}>
        {statusList.map(({ status, content }, idx) => (
          <TabPanel key={idx} value={current} index={idx}>
            {content}
          </TabPanel>
        ))}
      </Grid>
    </Grid>
  )
}
